import React, { useState, useEffect, useRef } from "react";
import { useApi } from "../hooks/useApi";
import Card from "./Card";
import { ChevronDown, ChevronUp, Shield } from "lucide-react";
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useModuleAvailability } from "../hooks/useModuleAvailability";

const SEVERITY_COLORS = {
    CRITICAL: "bg-red-100 text-red-800 border-red-300",
    HIGH: "bg-orange-100 text-orange-800 border-orange-300",
    MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-300",
    LOW: "bg-green-100 text-green-800 border-green-300",
};

const SOURCE_LABELS = {
    SubdomainDiscovered: "Subdomain Discovered",
    TlsVulnerability: "TLS Vulnerability",
    DnsConfigIssue: "DNS Config Issue",
    MissingSecurityHeader: "Missing Security Header",
    OpenPort: "Open Port",
    ExpiredCertificate: "Expired Certificate",
    CredentialBreach: "Credential Breach",
    DomainExposure: "Domain Exposure",
    ExposedSecret: "Exposed Secret",
    ExposedApiKey: "Exposed API Key",
    TyposquatDomain: "Typosquat Domain",
    CertStreamAlert: "CertStream Alert",
    DnsTwistDetection: "DNS Twist Detection",
    RansomwareVictimListing: "Ransomware Victim Listing",
    RansomwareGroupActivity: "Ransomware Group Activity",
    TelegramMention: "Telegram Mention",
    TelegramDataLeak: "Telegram Data Leak",
    TelegramCredentialSale: "Telegram Credential Sale",
};

const VendorExternalThreats = ({ vendorId }) => {
    const apiCall = useApi();
    // External threat events come from public feeds (ransomware.live and others): where the
    // installation cannot reach them the panel is not shown and fetches nothing (nor before the
    // flags load).
    const { loaded: modulesLoaded, isModuleAvailable } = useModuleAvailability();
    const available = modulesLoaded && isModuleAvailable("ransomwareFeeds");
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [expandedEvent, setExpandedEvent] = useState(null);
    const [pageSize, setPageSize] = usePageSize('vendorExternalThreats');
    const listTopRef = useRef(null);

    // Filters
    const [eventType, setEventType] = useState("");
    const [severity, setSeverity] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // Filters changed: the current page no longer exists in the new result set.
    useEffect(() => {
        setPage(1);
    }, [eventType, severity, dateFrom, dateTo]);

    // Fetching is driven by page/pageSize/filter state; the cancelled flag drops responses
    // from superseded requests so a stale page can't land after a newer one.
    useEffect(() => {
        if (!available) return undefined;
        let cancelled = false;
        const fetchEvents = async () => {
            try {
                setLoading(true);
                setError(null);
                const params = new URLSearchParams();
                if (eventType) params.set("eventType", eventType);
                if (severity) params.set("severity", severity);
                if (dateFrom) params.set("from", dateFrom);
                if (dateTo) params.set("to", dateTo);
                params.set("page", page);
                params.set("pageSize", pageSize);
                const result = await apiCall("GET", `/external-threat-events/vendor/${vendorId}?${params.toString()}`);
                if (cancelled) return;
                setEvents(result.items || result.events || (Array.isArray(result) ? result : []));
                setTotalCount(result.totalCount || 0);
                setTotalPages(result.totalPages || Math.ceil((result.totalCount || 0) / pageSize) || 1);
            } catch (err) {
                if (cancelled) return;
                setError(err);
                setEvents([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchEvents();
        return () => { cancelled = true; };
    }, [vendorId, apiCall, eventType, severity, dateFrom, dateTo, page, pageSize, available]);

    const parseMetadata = (metadataJson) => {
        if (!metadataJson) return null;
        try {
            return typeof metadataJson === "string" ? JSON.parse(metadataJson) : metadataJson;
        } catch {
            return null;
        }
    };

    if (!available) return null;

    return (
        <div>
            <p className="text-sm text-gray-500 mb-4">
                Unified timeline of external threat intelligence for this vendor, aggregated from dark web monitoring, credential breach detection, external surface scanning, brand protection, and secrets scanning. Events are correlated across sources to provide a complete picture of the vendor's external threat exposure.
            </p>

            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <select
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                >
                    <option value="">All Event Types</option>
                    <optgroup label="External Surface">
                        <option value="SubdomainDiscovered">Subdomain Discovered</option>
                        <option value="TlsVulnerability">TLS Vulnerability</option>
                        <option value="DnsConfigIssue">DNS Config Issue</option>
                        <option value="MissingSecurityHeader">Missing Security Header</option>
                        <option value="OpenPort">Open Port</option>
                        <option value="ExpiredCertificate">Expired Certificate</option>
                    </optgroup>
                    <optgroup label="Dark Web">
                        <option value="CredentialBreach">Credential Breach</option>
                        <option value="DomainExposure">Domain Exposure</option>
                    </optgroup>
                    <optgroup label="Secrets Scanning">
                        <option value="ExposedSecret">Exposed Secret</option>
                        <option value="ExposedApiKey">Exposed API Key</option>
                    </optgroup>
                    <optgroup label="Brand Protection">
                        <option value="TyposquatDomain">Typosquat Domain</option>
                        <option value="CertStreamAlert">CertStream Alert</option>
                        <option value="DnsTwistDetection">DNS Twist Detection</option>
                    </optgroup>
                    <optgroup label="Ransomware">
                        <option value="RansomwareVictimListing">Ransomware Victim Listing</option>
                        <option value="RansomwareGroupActivity">Ransomware Group Activity</option>
                    </optgroup>
                    <optgroup label="Telegram">
                        <option value="TelegramMention">Telegram Mention</option>
                        <option value="TelegramDataLeak">Telegram Data Leak</option>
                        <option value="TelegramCredentialSale">Telegram Credential Sale</option>
                    </optgroup>
                </select>
                <select
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                >
                    <option value="">All Severities</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                </select>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                    placeholder="From"
                />
                <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white"
                    placeholder="To"
                />
            </div>

            {loading ? (
                <Card>
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                </Card>
            ) : error ? (
                <Card>
                    <p className="text-red-600 text-sm">Failed to load external threat events.</p>
                </Card>
            ) : events.length === 0 ? (
                <Card>
                    <div className="text-center py-8 text-gray-500">
                        <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p>No external threat events found</p>
                    </div>
                </Card>
            ) : (
                <>
                    <div className="space-y-3" ref={listTopRef}>
                        {events.map((event, idx) => {
                            const sevClass = SEVERITY_COLORS[(event.severity || "").toUpperCase()] || SEVERITY_COLORS.MEDIUM;
                            const metadata = parseMetadata(event.metadataJson);
                            const isExpanded = expandedEvent === idx;

                            return (
                                <Card key={event.id || idx}>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${sevClass}`}>
                                                    {event.severity}
                                                </span>
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                                    {SOURCE_LABELS[event.eventType] || event.eventType}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {event.eventDate ? new Date(event.eventDate).toLocaleString() : ""}
                                                </span>
                                            </div>
                                            <h4 className="font-medium text-gray-900">{event.title}</h4>
                                            {event.description && (
                                                <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                            )}
                                        </div>
                                        {metadata && (
                                            <button
                                                onClick={() => setExpandedEvent(isExpanded ? null : idx)}
                                                className="ml-3 p-1 rounded hover:bg-gray-100 text-gray-400"
                                            >
                                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        )}
                                    </div>
                                    {isExpanded && metadata && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 text-sm">
                                            <h5 className="text-xs font-semibold text-gray-600 uppercase mb-2">Event Metadata</h5>
                                            <div className="space-y-1">
                                                {Object.entries(metadata).map(([key, value]) => (
                                                    <div key={key} className="flex gap-2">
                                                        <span className="text-gray-500 font-medium min-w-[120px]">{key}:</span>
                                                        <span className="text-gray-700">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        totalItems={totalCount}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        scrollTargetRef={listTopRef}
                        busy={loading}
                    />
                </>
            )}
        </div>
    );
};

export default VendorExternalThreats;
